import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import './Combobox.scss';

interface SingleProps {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
  multiple?: false;
}

interface MultiProps {
  label: string;
  multiple: true;
  /** Currently-selected options (in selection order). */
  selected: readonly string[];
  options: readonly string[];
  onToggle: (option: string) => void;
  /** Max number of selections; once reached, unchecked options are disabled. */
  max?: number;
}

type Props = SingleProps | MultiProps;

const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

const DROPDOWN_MAX_PX = 280;
const DROPDOWN_MIN_PX = 120;
const DROPDOWN_GAP_PX = 12;
// Approximate option height (16px vertical padding + ~20px line) and list
// chrome (8px padding + 2px border) used to estimate how many rows fit in a
// given available space. Used to decide whether to flip the dropdown above
// the input when there's only room for a handful of rows below.
const OPTION_HEIGHT_PX = 36;
const LIST_OVERHEAD_PX = 10;
// Flip the dropdown above the input only when fewer than 5 rows fit below;
// at exactly 5 (~190px) the below placement still reads fine.
const FLIP_UP_MAX_ITEMS = 4;
const SCROLL_TO_TOP_OFFSET_PX = 12;
// Wait for the on-screen keyboard to settle on mobile before scrolling so the
// `visualViewport` measurement reflects the keyboard-shrunk viewport.
const SCROLL_TO_TOP_DELAY_MS = 250;
const SCROLL_ROOT_SELECTOR = '.app__main';

/** Whether `el` is itself a scroll container (CSS `overflow-y: auto|scroll`).
 * On desktop `.app__main` is the scroll container; on mobile the document
 * scrolls and `.app__main` has the default `visible` overflow. The naive
 * `scrollHeight > clientHeight` test produces false positives on mobile
 * whenever route content is taller than the pane. */
function isScrollContainer(el: Element | null): boolean {
  if (!el) return false;
  const overflowY = getComputedStyle(el).overflowY;
  return overflowY === 'auto' || overflowY === 'scroll';
}

export default function Combobox(props: Props) {
  const { label, options } = props;
  const isMulti = props.multiple === true;
  // Empty-array fallback in single mode keeps the call sites bang-free —
  // they only read `selected` inside `isMulti` branches anyway.
  const selected: readonly string[] = isMulti ? props.selected : [];
  const value = isMulti ? '' : props.value;
  const max = isMulti ? props.max : undefined;
  const atMax = isMulti && max !== undefined && selected.length >= max;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const [placement, setPlacement] = useState<'above' | 'below'>('below');
  // Mirror of `placement` for reads inside callbacks/effects without
  // re-subscribing them. Locked at open-time; see the measure effect.
  const placementRef = useRef<'above' | 'below'>('below');
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();

  const filtered = useMemo(() => {
    if (!open || query === '') return options;
    const q = norm(query);
    return options.filter((o) => norm(o).includes(q));
  }, [open, query, options]);

  // Measure space between the input and the floor on open so the dropdown
  // never grows past the visible content area. On desktop, `.app__main`
  // scrolls internally and its bottom edge sits just above the footer — so
  // we clamp the dropdown to it. On mobile the document scrolls, so
  // `.app__main`'s bottom is the footer's position in document coords, not
  // a viewport-relative limit; clamping to it would shrink the dropdown for
  // no reason. The visualViewport floor (which moves with the iOS soft
  // keyboard) is always the right ceiling.
  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return undefined;
    const wrapper = wrapperRef.current;
    const main = wrapper.closest(SCROLL_ROOT_SELECTOR);
    let rafId = 0;
    // Placement is decided once on the first measure of an open cycle and
    // held for the rest of the cycle so the dropdown doesn't flip mid-use
    // (e.g. when scroll-to-top changes the available space below).
    let placementDecided = false;
    const itemsFit = (space: number) =>
      Math.floor((space - LIST_OVERHEAD_PX) / OPTION_HEIGHT_PX);
    const measure = () => {
      rafId = 0;
      const rect = wrapper.getBoundingClientRect();
      const vv = window.visualViewport;
      const viewportFloor = vv ? vv.offsetTop + vv.height : window.innerHeight;
      const viewportCeiling = vv ? vv.offsetTop : 0;
      const mainScrolls = isScrollContainer(main);
      const floorY =
        mainScrolls && main
          ? Math.min(main.getBoundingClientRect().bottom, viewportFloor)
          : viewportFloor;
      const ceilingY =
        mainScrolls && main
          ? Math.max(main.getBoundingClientRect().top, viewportCeiling)
          : viewportCeiling;
      const belowSpace = floorY - rect.bottom - DROPDOWN_GAP_PX;
      const aboveSpace = rect.top - ceilingY - DROPDOWN_GAP_PX;
      if (!placementDecided) {
        const belowItems = itemsFit(belowSpace);
        const aboveItems = itemsFit(aboveSpace);
        const flipUp = belowItems <= FLIP_UP_MAX_ITEMS && aboveItems > belowItems;
        const decided: 'above' | 'below' = flipUp ? 'above' : 'below';
        placementRef.current = decided;
        setPlacement(decided);
        placementDecided = true;
      }
      const space = placementRef.current === 'above' ? aboveSpace : belowSpace;
      setMaxHeight(Math.max(DROPDOWN_MIN_PX, Math.min(DROPDOWN_MAX_PX, space)));
    };
    const schedule = () => {
      if (rafId === 0) rafId = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    // Re-measure as the input scrolls within the content area — covers both
    // the smooth scroll-to-top below and any user-initiated scrolling.
    // `.app__main` is the scroll container on desktop; the document scrolls
    // on mobile, so we listen on both.
    main?.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('scroll', schedule, { passive: true });
    return () => {
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      main?.removeEventListener('scroll', schedule);
      window.removeEventListener('scroll', schedule);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [open]);

  // On open, scroll the input near the top of the scrollable content area so
  // the dropdown has maximum room above the on-screen keyboard. Delayed so
  // the keyboard has time to appear; only scrolls down (never pushes the
  // input upward into a position that hides it).
  useEffect(() => {
    if (!open || !wrapperRef.current) return undefined;
    const wrapper = wrapperRef.current;
    const id = window.setTimeout(() => {
      // When the dropdown is sitting above the input, scrolling the input
      // toward the top of the pane would eat the space we're actually using.
      if (placementRef.current === 'above') return;
      const main = wrapper.closest(SCROLL_ROOT_SELECTOR) as HTMLElement | null;
      const wrapperRect = wrapper.getBoundingClientRect();
      // On desktop `.app__main` is the scroll container; on mobile the
      // document scrolls. Use the overflow style — `scrollHeight >
      // clientHeight` is a false positive on mobile when route content
      // happens to overflow the pane (which never actually scrolls there).
      const mainScrolls = isScrollContainer(main);
      if (mainScrolls && main) {
        // Desktop: `.app__main` is the scroll container, anchored under the
        // header — its top stays fixed in viewport coords as it scrolls
        // internally, so it's a valid reference.
        const mainRect = main.getBoundingClientRect();
        const delta = wrapperRect.top - mainRect.top - SCROLL_TO_TOP_OFFSET_PX;
        if (delta > 0) main.scrollBy({ top: delta, behavior: 'smooth' });
        return;
      }
      // Mobile: the document scrolls, so `main`'s viewport-relative top moves
      // with it — using it as a reference produces a runaway delta after iOS
      // has already auto-scrolled the focused input into view. Anchor to the
      // viewport top directly instead.
      const delta = wrapperRect.top - SCROLL_TO_TOP_OFFSET_PX;
      if (delta > 0) window.scrollBy({ top: delta, behavior: 'smooth' });
    }, SCROLL_TO_TOP_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function commit(opt: string) {
    if (isMulti) {
      // Cap-at-max guard: ignore attempts to check a new option past the
      // limit. Unchecking is always allowed.
      if (atMax && !selected.includes(opt)) return;
      props.onToggle(opt);
      // Keep the dropdown open so users can pick further clubs in one
      // gesture; clear the query to surface the full list again.
      setQuery('');
      return;
    }
    props.onChange(opt);
    setOpen(false);
    setQuery('');
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[highlight]) {
        e.preventDefault();
        commit(filtered[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    } else if (e.key === 'Tab') {
      setOpen(false);
      setQuery('');
    }
  }

  // Single mode: show typed query while searching, otherwise the committed
  // value. Multi mode: show the typed query while searching, otherwise the
  // comma-joined selection list — empty input renders the placeholder so
  // the field looks like the others before the user picks anything.
  const multiDisplay = isMulti ? selected.join(', ') : '';
  const display = open && query !== '' ? query : isMulti ? multiDisplay : value;
  const isFilled = isMulti ? selected.length > 0 : !!value;

  return (
    <div
      className={`combobox form__field${isMulti ? ' combobox--multi' : ''}`}
      ref={wrapperRef}
    >
      <input
        type="text"
        className={`combobox__input${isFilled ? ' is-filled' : ''}`}
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && filtered[highlight] ? `${listId}-${highlight}` : undefined}
        autoComplete="off"
        spellCheck={false}
        placeholder={label}
        value={display}
        onFocus={() => {
          setOpen(true);
          setHighlight(0);
        }}
        // Click-to-toggle: clicking the input (chevron included, since it's
        // just a background-image on the input) flips the dropdown open/closed.
        // We only toggle when the input is *already* focused — on a first
        // click of a blurred input, mousedown fires before focus, so the
        // activeElement check fails and we let the focus handler open the
        // list normally instead of immediately reclosing it.
        onMouseDown={(e) => {
          if (document.activeElement !== e.currentTarget) return;
          if (open) {
            setOpen(false);
            setQuery('');
          } else {
            setOpen(true);
            setHighlight(0);
          }
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onKeyDown={handleKeyDown}
      />
      {open && filtered.length > 0 && (
        <ul
          id={listId}
          className={`combobox__list combobox__list--${placement}`}
          role="listbox"
          aria-multiselectable={isMulti || undefined}
          style={maxHeight !== null ? { maxHeight: `${maxHeight}px` } : undefined}
        >
          {filtered.map((opt, i) => {
            const isSelected = isMulti ? selected.includes(opt) : opt === value;
            const isDisabled = isMulti && atMax && !isSelected;
            return (
              <li
                key={opt}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={isSelected}
                aria-disabled={isDisabled || undefined}
                className={[
                  'combobox__option',
                  i === highlight ? 'is-highlighted' : '',
                  isDisabled ? 'is-disabled' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                // mousedown fires before the input's blur, so we can commit
                // before any focus race condition closes the list.
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (isDisabled) return;
                  commit(opt);
                }}
                onMouseEnter={() => setHighlight(i)}
              >
                {isMulti && (
                  <span
                    className={`combobox__check${isSelected ? ' is-checked' : ''}`}
                    aria-hidden="true"
                  />
                )}
                <span className="combobox__option-label">{opt}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
